"use client";
import Image from "next/image";
import { format } from "timeago.js";
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Pin, Heart } from "lucide-react";
import { UseTableState } from "@/components/table/dataTableStateContext";
import Link from "next/link";
import { unstable_ViewTransition as ViewTransition } from "react";

const getSortIcon = (column) => {
    const sorted = column.getIsSorted();
    if (sorted === false) return null;

    const onClick = (e) => {
        e.stopPropagation();
        column.clearSorting();
    };

    const arrow =
        sorted === "asc" ? (
            <ArrowDownWideNarrow onClick={onClick} className="ml-2 h-4 w-4 inline" />
        ) : (
            <ArrowUpNarrowWide onClick={onClick} className="ml-2 h-4 w-4 inline" />
        );
    return arrow;
};

export const columns = [
    {
        accessorKey: "id",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";
            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    ID {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            return <span className="text-xs font-light w-2">#{row.getValue("id")}</span>;
        },
    },
    {
        accessorKey: "pinned",
        header: ({ column }) => {
            return <Pin fill="var(--color-background)" />;
        },
        cell: ({ row }) => {
            const id = row.getValue("id");
            const { pinned, setPinned } = UseTableState();

            const pinnedIds = Array.from(pinned, ([_name, value]) => value);
            const fill = pinnedIds.includes(id) ? "var(--color-foreground)" : "var(--color-background)";

            return (
                <Pin
                    fill={fill}
                    onClick={() => {
                        if (pinnedIds.includes(id)) {
                            row.pin(false); // unpin
                            pinned.delete(row.id);
                        } else {
                            row.pin("top"); // pin to top
                            pinned.set(row.id, id);
                        }
                        setPinned(pinned);
                    }}
                />
            );
        },
    },
    {
        accessorKey: "favorited",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";
            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    <Heart fill="var(--color-background)" /> {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            let id = row.getValue("id");
            const { favorites, setFavorites } = UseTableState();
            const fill = favorites.has(id) ? "var(--color-foreground)" : "var(--color-background)";

            return (
                <Heart
                    fill={fill}
                    onClick={() => {
                        if (favorites.has(id)) {
                            favorites.delete(id);
                        } else {
                            favorites.add(id);
                        }
                        setFavorites(favorites);
                    }}
                />
            );
        },
    },
    {
        accessorKey: "icon",
        header: "",
        cell: ({ row }) => {
            return (
                <div className="w-10 h-10 flex items-center justify-center">
                    <img
                        className="rounded-md"
                        src={`${row.getValue("icon")}`}
                        alt={`A small image of ${row.getValue("name")}`}
                        style={{ filter: "drop-shadow(0 0 6px var(--color-foreground))" }}
                    />
                </div>
            );
        },
    },
    {
        accessorKey: "name",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";
            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Name {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            const id = row.getValue("id");
            const name = row.getValue("name");
            return (
                <ViewTransition name={`item-title-${id}`}>
                    <Link href={`/osrs/items/${id}`} className="inline-block">
                        <span>{name}</span>
                    </Link>
                </ViewTransition>
            );
        },
        sortingFn: "alphanumeric",
    },
    {
        accessorKey: "high",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Buy Price {arrow}
                </span>
            );
        },
    },
    {
        accessorKey: "low",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Sell Price {arrow}
                </span>
            );
        },
    },
    {
        accessorFn: (row) => row.high - row.low,
        id: "profit",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Profit {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            const profit = Number(row.getValue("profit"));
            const displayColor = profit > 0 ? "text-green-800" : profit == 0 ? "" : "text-red-800";
            const fontWeight = profit == 0 ? "" : "font-medium";
            return <span className={`${fontWeight} ${displayColor}`}>{profit}</span>;
        },
    },
    {
        accessorFn: (row) => ((row.high - row.low) / row.low) * 100,
        id: "roi",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    ROI {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            const roi = Number(row.getValue("roi"));
            const profit = Number(row.getValue("profit"));
            const displayColor = profit > 0 ? "text-green-800" : profit == 0 ? "" : "text-red-800";
            const fontWeight = profit == 0 ? "" : "font-medium";
            return <span className={`${fontWeight} ${displayColor}`}>{roi.toFixed(2)}%</span>;
        },
    },
    {
        accessorKey: "volume",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Volume {arrow}
                </span>
            );
        },
    },
    {
        accessorKey: "limit",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Limit {arrow}
                </span>
            );
        },
    },
    {
        accessorKey: "highTime",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Latest Buy {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            const date = new Date(Number(row.getValue("highTime")) * 1000);
            return <span title={date.toTimeString()}>{format(date)}</span>;
        },
    },
    {
        accessorKey: "lowTime",
        header: ({ column }) => {
            const arrow = getSortIcon(column);
            const sorted = column.getIsSorted() === "asc";

            return (
                <span className="flex flex-row items-center" onClick={() => column.toggleSorting(sorted)}>
                    Latest Sell {arrow}
                </span>
            );
        },
        cell: ({ row }) => {
            const date = new Date(Number(row.getValue("lowTime")) * 1000);
            return <span title={date.toTimeString()}>{format(date)}</span>;
        },
    },
];
